import { cn } from "@/utils";
import {
  Ban,
  Bot,
  Check,
  Loader2,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { HarnessOutlineView } from "./HarnessOutlineView";
import { HarnessAgentEntry, HarnessAgentStatus } from "./harnessRailItems";

const STATUS: Record<
  HarnessAgentStatus,
  { Icon: LucideIcon; tone: string; label: string; spin?: boolean }
> = {
  queued: { Icon: Loader2, tone: "text-n-40", label: "queued", spin: true },
  running: {
    Icon: Loader2,
    tone: "text-muted-foreground",
    label: "running",
    spin: true,
  },
  done: { Icon: Check, tone: "text-lc-active", label: "done" },
  failed: { Icon: TriangleAlert, tone: "text-lc-blocked", label: "failed" },
  stopped: { Icon: Ban, tone: "text-muted-foreground", label: "stopped" },
};

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function countBy(agents: HarnessAgentEntry[]): string {
  const counts = new Map<HarnessAgentStatus, number>();
  for (const a of agents) counts.set(a.status, (counts.get(a.status) ?? 0) + 1);
  return (["running", "queued", "done", "failed", "stopped"] as const)
    .filter((s) => counts.get(s))
    .map((s) => `${counts.get(s)} ${STATUS[s].label}`)
    .join(" · ");
}

/**
 * The agent board: every sub-agent a segment launched, one row each, pulled
 * out of the linear rail. A row carries the agent's name, a status chip that
 * follows its work item (queued → running → done/failed), a live tool-count
 * ticker and latest-activity gist while it runs, and its duration once done.
 * Expanding a row shows the agent's own outline when the API attributes rows
 * to it, otherwise its launch brief and result summary.
 */
export function HarnessAgentBoard({
  agents,
  live = false,
  depth = 0,
}: {
  agents: HarnessAgentEntry[];
  live?: boolean;
  depth?: number;
}) {
  const active = agents.some(
    (a) => a.status === "running" || a.status === "queued",
  );
  return (
    <div data-testid="harness-agent-board">
      <div className="flex items-center gap-2 px-4 py-2">
        <span className="relative z-[1] flex size-6 shrink-0 items-center justify-center bg-background">
          <Bot className="size-[18px] text-n-80" />
        </span>
        <span
          className={cn(
            "min-w-0 flex-1 truncate font-sans text-sm text-n-70",
            active && live && "shimmer text-muted-foreground",
          )}
        >
          {agents.length} agent{agents.length === 1 ? "" : "s"}
          <span className="ml-2 text-n-50">{countBy(agents)}</span>
        </span>
      </div>
      <div className="mb-2 ml-12 mr-4 rounded-md bg-[color-mix(in_oklab,var(--n-10)_45%,var(--card))] p-1">
        {agents.map((agent) => (
          <HarnessAgentRow key={agent.key} agent={agent} depth={depth} />
        ))}
      </div>
    </div>
  );
}

function HarnessAgentRow({
  agent,
  depth,
}: {
  agent: HarnessAgentEntry;
  depth: number;
}) {
  const [open, setOpen] = useState(false);
  const { Icon, tone, spin } = STATUS[agent.status];
  const busy = agent.status === "running" || agent.status === "queued";
  const gist = busy
    ? agent.summary || (agent.lastTool ? `Using ${agent.lastTool}` : undefined)
    : agent.error || undefined;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="harness-agent-row"
        className="flex w-full flex-col gap-0.5 rounded-sm px-2.5 py-1.5 text-left font-sans text-xs transition-colors hover:bg-card"
      >
        <span className="flex w-full items-center gap-2">
          <Icon
            className={cn("size-3 shrink-0", tone, spin && "animate-spin")}
          />
          <span className="min-w-0 flex-1 truncate text-sm text-n-80">
            {agent.name}
          </span>
          {agent.subagentType && agent.subagentType !== "general-purpose" && (
            <span className="shrink-0 rounded-sm bg-n-10 px-1.5 py-px font-mono text-[11px] text-n-60">
              {agent.subagentType}
            </span>
          )}
          {agent.toolUses != null && (
            <span className="shrink-0 font-mono text-n-50">
              {agent.toolUses} tool{agent.toolUses === 1 ? "" : "s"}
            </span>
          )}
          {!busy && agent.durationMs != null && (
            <span className="shrink-0 font-mono text-n-50">
              {formatDuration(agent.durationMs)}
            </span>
          )}
        </span>
        {gist && (
          <span
            className={cn(
              "w-full truncate pl-5 text-n-50",
              agent.status === "failed" && "text-lc-blocked",
            )}
          >
            {gist}
          </span>
        )}
      </button>
      {open && (
        <div className="mx-2.5 mb-1.5 rounded-md bg-background">
          {agent.outline ? (
            <HarnessOutlineView outline={agent.outline} depth={depth + 1} />
          ) : (
            <div className="p-3 font-sans text-xs text-n-60">
              {agent.prompt && (
                <p className="line-clamp-4 whitespace-pre-line text-pretty">
                  {agent.prompt}
                </p>
              )}
              {!busy && agent.summary && (
                <p className="mt-2 line-clamp-6 whitespace-pre-line text-pretty text-n-70">
                  {agent.summary}
                </p>
              )}
              {!agent.prompt && !agent.summary && (
                <p className="text-n-50">No detail reported for this agent.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
